import { injectable } from 'inversify';
import { Routing as NeoRouting } from '@singularsystems/neo-core';
import { Routing } from '@singularsystems/neo-react';

import { AppService, AppTypes } from './AppService';

import NotFound from '../Components/Application/404NotFound';
import LoggedOut from '../Views/Security/LoggedOut';
import OidcLoginRedirect from '../Views/Security/OidcLoginRedirect';
import RegisterView from '../Views/Security/RegisterView';
import SilentSignInView from 'st-common/App/Views/SilentSignInView';

import { ChangePasswordRoute, IdentityPureRoutes, IdentityCommonRoutes } from 'st-identity/IdentityModule';
import { AwardsPureRoutes, MyAwardsRoute, ReleaseRoute } from 'st-awards/AwardsModule';
import { PortfolioViewRoute, TradeHistoryRoute, BrokerDetailsRoute, PureRoutes as TransactionsPureRoutes } from 'st-transactions/TransactionsModule';
import { ContactDetailsRoute, SecondmentsRoute, PureRoutes as ParticipantsPureRoutes, DocumentsRoute, BeneficiariesRoute, PreferredCommunicationMethodRoute } from 'st-participants/ParticipantsModule';
import { ContactUsRoute, PureRoutes as ClientsPureRoutes } from 'st-clients/ClientsModule';
import { AllocationManagerRoute } from 'st-allocationmanager/AllocationManagerModule';
import Home from '../Views/Home';
import AllocationInformationView from 'st-allocationmanager/Views/AllocationInformationView';
import { Config } from '../Models/Config/Config';

interface IRouteExtensions {
    requiresParticipant?: boolean;
}

export interface IAppRoute extends NeoRouting.IRoute, IRouteExtensions {
    path: string;
    component: any;
}

export interface IAppMenuItem extends NeoRouting.IMenuRoute, IRouteExtensions {
    showMenuItem?: () => boolean;
}

export const SilentSignInRoute = {
    path: "/silentsignin", component: SilentSignInView, allowAnonymous: false
};

@injectable()
export class RouteService {
    private routeTree: NeoRouting.IMenuRoute[] = [];
    private pureRoutes: NeoRouting.IRoute[] = [];
    private routeProvider: Routing.RouteProvider;

    constructor(private config = AppService.get(AppTypes.Shared.Config)) {
        this.createRouteTree();
        this.createPureRoutes();
        this.routeProvider = new Routing.RouteProvider(this.routeTree, this.pureRoutes, NotFound);
    }

    public readonly registerRoute = { path: this.config.registerPath, component: RegisterView, allowAnonymous: true };

    /**
     * Gets the routes provider
     */
    public get routes(): Routing.RouteProvider {
        return this.routeProvider;
    }

    /**
     * Create the menu routes tree
     */
    private createRouteTree() {
        this.routeTree = [
            {
                path: this.config.dashboardPath, name: "Home", icon: "home", component: Home
            },
            {
                name: "Awards", icon: "award", children: [
                    ReleaseRoute,
                    MyAwardsRoute,
                    AllocationManagerRoute, 
                ]
            },
            new Routing.MenuRoute(PortfolioViewRoute, {
                name: "Portfolio", icon: "chart-line", children: [
                    new Routing.MenuRoute(PortfolioViewRoute, { name: "Portfolio Details" }),
                    new Routing.MenuRoute(TradeHistoryRoute, { name: "Trade History" }),
                    MyAwardsRoute,
                ]
            }),
            new Routing.MenuRoute(ContactDetailsRoute, {
                name: "Profile", icon: "user", children: [
                    new Routing.MenuRoute(DocumentsRoute, { name: "My Documents" }),
                    new Routing.MenuRoute(ContactDetailsRoute, { name: "Contact details" }),
                    new Routing.MenuRoute(BrokerDetailsRoute, { name: "Broker account details" }),
                    new Routing.MenuRoute(SecondmentsRoute, { name: "Global locations" }),
                    new Routing.MenuRoute(ChangePasswordRoute, { name: "Change password" }),
                    new Routing.MenuRoute(BeneficiariesRoute, { name: "Beneficiaries" }),
                    new Routing.MenuRoute(PreferredCommunicationMethodRoute, { name: "Communication preferences" }),
                    IdentityCommonRoutes.TermsAndConditionsRoute
                ]
            }),
            new Routing.MenuRoute(ContactUsRoute, { name: "Contact us", icon: "far-address-book" })
        ];        
    }

    /**
     * Create the pure routes list
     */
    private createPureRoutes() {
        this.pureRoutes = [
            {
                path: Config.loginRedirectRoute, component: OidcLoginRedirect, allowAnonymous: true
            },
            {
                path: "/loggedout", component: LoggedOut, allowAnonymous: true
            },
            {
                path: "/supportandtraining", component: AllocationInformationView, allowAnonymous: false
            },
            SilentSignInRoute,
            ...AwardsPureRoutes,
            ...TransactionsPureRoutes,
            ...IdentityPureRoutes,
            ...ParticipantsPureRoutes,
            ...ClientsPureRoutes
        ]
    };
}
